// src/sim/hiding/enter-exit.ts — P3 hide-toggle transitions (HIDE-03, HIDE-04, D-21, D-25).
//
// Invariants:
//   - Pure: returns { next, events }; never mutates inputs. Free+null or caught
//     returns the ORIGINAL entity reference (no-op) so tick.ts can short-circuit.
//   - Exit clears peekEndTick even when exiting from 'peeking' — peek state
//     cannot survive a deliberate unhide toggle.
//   - Entry writes EITHER hidingSpotId (Tiled spot) XOR floorSpot (on-floor
//     synthetic) based on `activeSpot.kind === 'on-floor'`. The two storage
//     slots are mutually exclusive by contract (see world.ts Entity comments).
//
// Source: .planning/phases/03-core-sim-primitives/03-CONTEXT.md D-21, D-25.

import type { Entity } from '../../shared/types/world';
import type { HidingSpotAuth } from '../../shared/types/map';
import type { GameEvent } from '../../shared/types/events';

export interface HideToggleResult {
  next: Entity;
  events: GameEvent[];
}

/**
 * Apply a hide-toggle one-shot. When `entity.status === 'free'` and an
 * `activeSpot` is provided, enter hiding and emit `hiding-entered`. When
 * `entity.status` is `'hiding'` or `'peeking'`, exit and emit `hiding-exited`
 * with the PREVIOUSLY-active spot id (from `hidingSpotId` or `floorSpot.id`).
 * All other combinations are no-ops.
 */
export function applyHideToggle(
  entity: Entity,
  activeSpot: HidingSpotAuth | null,
  currentTick: number,
): HideToggleResult {
  if (entity.status === 'free' && activeSpot !== null) {
    const isFloor = activeSpot.kind === 'on-floor';
    // UAT Test 7 follow-up: snap the hider's position to the spot centre on
    // entry so the player doesn't stay offset behind a tree/pot. On-floor
    // spots carry the hider's own current position as their `position`, so
    // the snap is a no-op for that kind (Vec2 copy preserves identity).
    const next: Entity = {
      ...entity,
      status: 'hiding',
      position: { x: activeSpot.position.x, y: activeSpot.position.y },
      hidingSpotId: isFloor ? null : activeSpot.id,
      floorSpot: isFloor ? activeSpot : null,
    };
    return {
      next,
      events: [
        { type: 'hiding-entered', entityId: entity.id, spotId: activeSpot.id, tick: currentTick },
      ],
    };
  }

  if (entity.status === 'hiding' || entity.status === 'peeking') {
    // `?? 'unknown'` defends against a data regression where both slots are
    // null while status says we're hiding — the event still fires with a
    // traceable spotId rather than crashing the event bus.
    const spotId = entity.hidingSpotId ?? entity.floorSpot?.id ?? 'unknown';
    const next: Entity = {
      ...entity,
      status: 'free',
      hidingSpotId: null,
      floorSpot: null,
      peekEndTick: null,
    };
    return {
      next,
      events: [
        { type: 'hiding-exited', entityId: entity.id, spotId, tick: currentTick },
      ],
    };
  }

  return { next: entity, events: [] };
}
